import type { ReactNode } from "react"

import type { TOrganizationAbility } from "@/lib/auth/organization-abilities"
import { useOrganization } from "@/store/organization"

type TOrganizationGuardProps = {
  action: Parameters<TOrganizationAbility["can"]>[0]
  subject: Parameters<TOrganizationAbility["can"]>[1]
  children: ReactNode
  fallback?: ReactNode
}

/**
 * OrganizationGuard - HOC to check CASL permissions for organization-scoped actions
 *
 * @example
 * ```tsx
 * <OrganizationGuard action="create" subject="People">
 *   <CreatePersonButton />
 * </OrganizationGuard>
 * ```
 */
export function OrganizationGuard({
  action,
  subject,
  children,
  fallback = null,
}: TOrganizationGuardProps) {
  const { ability } = useOrganization()

  if (!ability) {
    return fallback
  }

  // @ts-expect-error - CASL type inference is complex, verified at runtime
  const hasPermission = ability.can(action, subject)

  if (!hasPermission) {
    return fallback
  }

  return children
}
